import { Tracking, TrackingDetails } from '../types/tracking-types';

/**
 * Gets all the tracking events from all parcels and finds the latest scan
 * @param {Tracking} t The tracking information
 * @returns {Date} Returns the date of the of the latest scan if possible else returns null.
 */
export function GetLastTrackingEventTimeStamp(t: Tracking): Date | null {
  try {
    if (t.parcels) {
      const trackingDetails = Object.values(t.parcels);
      const events = trackingDetails
        .map((td) => td.events.map((event) => new Date(event.timestamp)))
        .reduce((a, b) => a.concat(b));
      return events.sort((a, b) => b.getTime() - a.getTime())[0];
    }
  } catch (e) {
    console.error('Unable to parse tracking details within GetLastTrackingEventTimeStamp function. %o', e);
  }

  return null;
}

/**
 * Gets tracking events from a single parcel and finds the latest scan
 * @param {TrackingDetails} td The tracking information
 * @returns {Date} Returns the date of the of the latest scan if possible else returns null.
 */
export function GetLastTrackingEventTimeStampSingleParcel(td: TrackingDetails): Date | null {
  try {
    if (td) {
      const events = td.events.map((event) => new Date(event.timestamp));
      return events.sort((a, b) => b.getTime() - a.getTime())[0];
    }
  } catch (e) {
    console.error('Unable to parse tracking details within GetLastTrackingEventTimeStampSingleParcel function. %o', e);
  }

  return null;
}

/**
 * Returns a timestamp in the past based off the number of days entered
 * @param {number} numOfDays The number of days in the past you want the time stamp for
 * @returns {number} Returns the timestamp.
 */
export function GetRelativeTimestampByDays(numOfDays: number): number {
  return new Date().getTime() - numOfDays * 24 * 60 * 60 * 1000;
}

/**
 * Returns whether the estimated delivery date has passed for a given tracking object.
 * Returns false if unable to determine.
 * @param {Tracking} t The tracking event
 * @returns {boolean} Returns whether the delivery date has passed.
 */
export function HasEstimatedDeliveryDatePassed(estimatedDeliveryDateString: string | null): boolean {
  try {
    if (estimatedDeliveryDateString) {
      const estimatedDeliveryDate = new Date(estimatedDeliveryDateString);

      return estimatedDeliveryDate.getTime() < new Date().getTime();
    }
  } catch (e) {
    console.error('Unable to determine if the estimated delivery date has passed. %o', e);
  }

  return false;
}
